import React from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AddressSearchFiled from '@/components/common/organism/AddressSearchFiled';
import OnlineAccessFields from '@/pages/Seminar/components/form/OnlineAccessFields';
import SessionListField from '@/pages/Seminar/components/form/SessionListField';
import DateTimePicker from '@/components/common/datePicker/DateTimePicker';
import StationSearchField from '@/components/common/station/StationSearchField';
import {
  ChoiceCard,
  ChoiceCardGroup,
  ErrorMessage,
  FORM_COLOR,
  FieldBlock,
  GuideLines,
  Label,
  NoticeBox,
  StyledInput,
  TagOption,
  TagSelector,
  TwoColumnGrid,
} from '@/pages/Seminar/components/form/formStyles';
import {
  SCHEDULE_OPTIONS,
  SeminarFormatValue,
  VOD_SCHEDULE_OPTIONS,
  parseLocal,
} from '@/pages/Seminar/components/form/seminarFormUtils';
import { oneMinuteAfter } from '@/components/common/datePicker/pickerUtils';
import { useSeminarForm } from '@/pages/Seminar/components/form/steps/SeminarFormContext';

/** 결제자가 있을 때 칸을 통째로 잠그는 틀(안의 입력·버튼이 모두 disabled) */
export const LockFieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:disabled {
    opacity: 0.6;
  }
`;

/** 주소 검색 상자 — 공용 주소 검색을 52px 상자형으로 */
const AddressBox = styled.div<{ hasError?: boolean }>`
  > div:first-of-type {
    border: 1px solid ${({ hasError }) => (hasError ? FORM_COLOR.error : FORM_COLOR.border)} !important;
    border-radius: 12px !important;
    padding: 0 14px !important;
  }

  > div:first-of-type svg {
    color: ${FORM_COLOR.primary};
    flex: 0 0 auto;
  }

  > div:first-of-type p,
  > div:first-of-type span,
  > div:first-of-type div {
    font-size: 15px !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const FORMAT_CARDS: Array<{ value: SeminarFormatValue; label: string; desc: string }> = [
  { value: 'OFFLINE', label: '오프라인', desc: '정해진 장소에서' },
  { value: 'LIVE', label: '라이브', desc: '실시간 온라인' },
  { value: 'VOD', label: '동영상', desc: '녹화 영상 시청' },
];

/** 2단계 — 진행 방식·일정(당일/기간/회차/상시)·장소 또는 접속 링크·가까운 지하철역 */
const StepSchedulePlace: React.FC = () => {
  const {
    control, errors, values, setValue, watch, career, format, schedule, openSale, isOnlineFormat, showAddress,
    lockPriceSchedule, today, onFormatChange, onScheduleChange, onAddressChange, stationObjects, onStationsChange,
    refundGuide, showMidwayCancelNotice, midwayNoticeText, vodRefundNotice, openSaleSaved,
  } = useSeminarForm();

  return (
    <>
      {/* 진행 방식 — 커리어: 오프라인/라이브/동영상, 일반: 오프라인/온라인(isOnline) */}
      <FieldBlock>
        <Label as="div">진행 방식<span>*</span></Label>
        {career ? (
          <LockFieldset disabled={lockPriceSchedule}>
            <ChoiceCardGroup columns={3} role="radiogroup" aria-label="진행 방식">
              {FORMAT_CARDS.map((card) => (
                <ChoiceCard
                  key={card.value}
                  type="button"
                  role="radio"
                  aria-checked={format === card.value}
                  selected={format === card.value}
                  onClick={() => { if (format !== card.value) onFormatChange(card.value); }}
                >
                  <b>{card.label}</b>
                  {card.desc}
                </ChoiceCard>
              ))}
            </ChoiceCardGroup>
          </LockFieldset>
        ) : (
          <ChoiceCardGroup columns={2} role="radiogroup" aria-label="진행 방식">
            <ChoiceCard type="button" role="radio" aria-checked={!values.isOnline} selected={!values.isOnline}
              onClick={() => setValue('isOnline', false, { shouldValidate: true })}>
              <b>오프라인</b>
              정해진 장소에서
            </ChoiceCard>
            <ChoiceCard type="button" role="radio" aria-checked={!!values.isOnline} selected={!!values.isOnline}
              onClick={() => setValue('isOnline', true, { shouldValidate: true })}>
              <b>온라인</b>
              주소 없이 온라인으로
            </ChoiceCard>
          </ChoiceCardGroup>
        )}
      </FieldBlock>

      {/* 일정 — 결제자가 있으면 통째로 잠근다(fieldset disabled — 날짜 선택기·회차 입력 포함) */}
      <FieldBlock>
        <Label as="div">일정<span>*</span></Label>
        <LockFieldset disabled={lockPriceSchedule}>
          {career && (
            <TagSelector role="radiogroup" aria-label={format === 'VOD' ? '동영상 일정' : '일정 유형'}>
              {(format === 'VOD' ? VOD_SCHEDULE_OPTIONS : SCHEDULE_OPTIONS).map((option) => (
                <TagOption
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={schedule === option.value}
                  selected={schedule === option.value}
                  onClick={() => { if (schedule !== option.value) onScheduleChange(option.value); }}
                >
                  {option.label}
                </TagOption>
              ))}
            </TagSelector>
          )}
          {career && format === 'VOD' && (
            <GuideLines>{openSale ? '종료일 없이 계속 팔아요. 판매 시작일만 정하면 돼요.' : '정한 시청 기간 동안만 팔고 볼 수 있어요.'}</GuideLines>
          )}

          {schedule === 'SINGLE' && (
            <TwoColumnGrid>
              <div data-field="startDateTime" style={{ minWidth: 0 }}>
                <Controller
                  name="startDateTime"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      mode="datetime"
                      title="시작 일시"
                      placeholder="시작 일시"
                      value={field.value || ''}
                      onChange={(value: string) => field.onChange(value)}
                      min={new Date()}
                      hasError={!!errors.startDateTime}
                    />
                  )}
                />
              </div>
              <div data-field="endDateTime" style={{ minWidth: 0 }}>
                <Controller
                  name="endDateTime"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      mode="datetime"
                      icon="clock"
                      title="종료 일시"
                      placeholder="종료 일시"
                      value={field.value || ''}
                      onChange={(value: string) => field.onChange(value)}
                      // 종료는 시작보다 뒤 — 시작이 없으면 지금 이후
                      min={oneMinuteAfter(parseLocal(values.startDateTime)) ?? new Date()}
                      // 지름길 — 시작 날짜가 골라진 채 ② 시간(시작 + 2시간 미리 선택)으로 연다
                      shortcutStart={values.startDateTime || null}
                      hasError={!!errors.endDateTime}
                    />
                  )}
                />
              </div>
            </TwoColumnGrid>
          )}
          {schedule === 'SINGLE' && errors.startDateTime && <ErrorMessage>{errors.startDateTime.message}</ErrorMessage>}
          {schedule === 'SINGLE' && errors.endDateTime && <ErrorMessage>{errors.endDateTime.message}</ErrorMessage>}

          {schedule === 'PERIOD' && (
            <>
              {career && <GuideLines>{format === 'VOD' ? '시청 기간' : '기간'}</GuideLines>}
              {/* 칸 하나로 시작일 → 종료일. 검증 이동 대상 이름(periodStartDate·periodEndDate)은 그대로 둔다 */}
              <div data-field="periodStartDate" style={{ minWidth: 0 }}>
                <div data-field="periodEndDate">
                  <DateTimePicker
                    mode="range"
                    title={format === 'VOD' ? '시청 기간' : '기간'}
                    subtitle="시작일 → 종료일 순서로 눌러 주세요"
                    placeholder="시작일 ~ 종료일"
                    start={values.periodStartDate || ''}
                    end={values.periodEndDate || ''}
                    onChange={(startDate: string, endDate: string) => {
                      setValue('periodStartDate', startDate, { shouldValidate: true, shouldDirty: true });
                      setValue('periodEndDate', endDate, { shouldValidate: true, shouldDirty: true });
                    }}
                    minDate={career ? today : undefined}
                    hasError={!!errors.periodStartDate || !!errors.periodEndDate}
                  />
                </div>
              </div>
              {errors.periodStartDate && <ErrorMessage>{errors.periodStartDate.message}</ErrorMessage>}
              {errors.periodEndDate && <ErrorMessage>{errors.periodEndDate.message}</ErrorMessage>}
            </>
          )}

          {schedule === 'OPEN' && (
            <>
              <GuideLines>판매 시작일</GuideLines>
              <div data-field="periodStartDate" style={{ minWidth: 0 }}>
                <Controller
                  name="periodStartDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      mode="date"
                      title="판매 시작일"
                      subtitle="이날부터 팔아요"
                      placeholder="판매 시작일"
                      value={field.value || ''}
                      onChange={(value: string) => field.onChange(value)}
                      minDate={today}
                      hasError={!!errors.periodStartDate}
                    />
                  )}
                />
              </div>
              {errors.periodStartDate && <ErrorMessage>{errors.periodStartDate.message}</ErrorMessage>}
            </>
          )}

          {schedule === 'SESSIONS' && (
            <div data-field="sessions" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Controller
                name="sessions"
                control={control}
                render={({ field }) => (
                  <SessionListField
                    sessions={field.value || []}
                    onChange={(next) => field.onChange(next)}
                    error={errors.sessions?.message}
                  />
                )}
              />
            </div>
          )}
        </LockFieldset>
        {lockPriceSchedule && (
          <GuideLines>{'결제한 분이 있어 일정·장소·참가비는 바꿀 수 없어요.\n바꾸려면 폐강 후 다시 등록해 주세요.'}</GuideLines>
        )}
        <GuideLines>{refundGuide}</GuideLines>
        {showMidwayCancelNotice && (
          <NoticeBox>
            <ErrorOutlineIcon aria-hidden />
            <span>{midwayNoticeText}</span>
          </NoticeBox>
        )}
        {vodRefundNotice && (
          <NoticeBox>
            <ErrorOutlineIcon aria-hidden />
            <span>{vodRefundNotice}</span>
          </NoticeBox>
        )}
      </FieldBlock>

      {/* 접속 — 커리어 라이브·동영상 */}
      {isOnlineFormat && (format === 'LIVE' || format === 'VOD') && (
        <FieldBlock>
          <OnlineAccessFields
            format={format}
            url={values.onlineUrl || ''}
            guide={values.onlineGuide || ''}
            onUrlChange={(value) => setValue('onlineUrl', value, { shouldValidate: true })}
            onGuideChange={(value) => setValue('onlineGuide', value, { shouldValidate: true })}
            urlError={errors.onlineUrl?.message}
            guideError={errors.onlineGuide?.message}
            openSale={format === 'VOD' && (openSaleSaved || schedule === 'OPEN')}
          />
        </FieldBlock>
      )}

      {/* 장소 — 결제자가 있으면 주소·장소명·지하철역도 잠근다(접속 링크는 언제든 바꿀 수 있다) */}
      {showAddress && (
        <FieldBlock>
          <Label as="div">장소<span>*</span></Label>
          <LockFieldset disabled={lockPriceSchedule}>
            <AddressBox data-field="address" hasError={!!errors.address}>
              <AddressSearchFiled
                onChange={onAddressChange}
                defaultValue={watch('address') || undefined}
                height={52}
                width={'100%'}
                border={'none'}
                margin="0"
                icon={<PlaceOutlinedIcon aria-hidden />}
                formPopup
              />
            </AddressBox>
            {errors.address && <ErrorMessage>{errors.address.message}</ErrorMessage>}
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <StyledInput
                  {...field}
                  placeholder="상세 장소 (예: 3층 세미나룸)"
                  aria-label="상세 장소"
                  data-field="location"
                  aria-invalid={!!errors.location}
                  hasError={!!errors.location}
                />
              )}
            />
            {errors.location && <ErrorMessage>{errors.location.message}</ErrorMessage>}
          </LockFieldset>
        </FieldBlock>
      )}

      {showAddress && career && (
        <FieldBlock>
          <Label as="div">
            가까운 지하철역
            <small>선택</small>
          </Label>
          <StationSearchField
            value={stationObjects}
            onChange={onStationsChange}
            disabled={lockPriceSchedule}
            dataField="stations"
          />
        </FieldBlock>
      )}

      {!career && values.isOnline && (
        <GuideLines>온라인 세미나는 주소 없이 목록에 &quot;온라인&quot;으로 보여요.</GuideLines>
      )}
    </>
  );
};

export default StepSchedulePlace;
